// The portal's tables, one look everywhere: a card with a light header, a
// skeleton while loading and a helpful empty state. On phones each row turns
// into a card and every cell is labelled with its column (see .mh-table in
// index.css); the labels are copied from the header, so row components stay
// plain <tr>/<td>.
import React, { useLayoutEffect, useRef } from "react";
import { Inbox } from "lucide-react";

export const EmptyState = ({ icon: Icon = Inbox, title = "Nothing here yet", text, action }) => (
  <div className="flex flex-col items-center px-6 py-14 text-center">
    <span className="flex h-12 w-12 items-center justify-center rounded-full bg-gray-100 text-gray-500">
      <Icon className="h-6 w-6" />
    </span>
    <p className="mt-4 font-semibold text-gray-900">{title}</p>
    {text && <p className="mt-1 max-w-md text-sm text-gray-500">{text}</p>}
    {action && <div className="mt-5">{action}</div>}
  </div>
);

const Skeleton = ({ columns }) => (
  <div className="divide-y divide-gray-100" aria-busy="true" aria-label="Loading">
    {Array.from({ length: 4 }).map((_, row) => (
      <div key={row} className="flex gap-4 px-4 py-4">
        {Array.from({ length: Math.min(columns, 5) }).map((__, col) => (
          <div key={col} className="h-4 flex-1 animate-pulse rounded bg-gray-100" />
        ))}
      </div>
    ))}
  </div>
);

// head: [{ id, label }]. A label of "" (an actions column) shows no name on phones.
export default function DataTable({ head, children, isLoading = false, isEmpty = false, empty = {}, caption }) {
  const tableRef = useRef(null);

  // Every cell gets its column name for the phone layout.
  useLayoutEffect(() => {
    const table = tableRef.current;
    if (!table) return;
    table.querySelectorAll("tbody > tr").forEach((tr) => {
      Array.from(tr.children).forEach((cell, i) => {
        if (cell.colSpan > 1) return;
        const label = head[i]?.label ?? "";
        if (cell.getAttribute("data-label") !== label) cell.setAttribute("data-label", label);
      });
    });
  });

  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
      {isLoading ? (
        <Skeleton columns={head.length} />
      ) : isEmpty ? (
        <EmptyState {...empty} />
      ) : (
        <div className="overflow-x-auto">
          <table ref={tableRef} className="mh-table w-full text-left text-sm text-gray-700">
            {caption && <caption className="sr-only">{caption}</caption>}
            <thead className="border-b border-gray-200 bg-gray-50/80">
              <tr>
                {head.map((h) => (
                  <th key={h.id} scope="col" className="whitespace-nowrap px-4 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                    {h.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">{children}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}
